/**
 * Stale-while-revalidate cache for Home featured rows (sessionStorage).
 */

const CACHE_KEY = 'maestro_home_featured_v1';
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export interface HomeFeaturedCachePayload {
  newReleases: unknown[] | null;
  editorPicks: unknown[] | null;
  featuredPlaylists: unknown[] | null;
  pickedForYou: unknown[] | null;
  popularGenre: unknown[] | null;
  popularGenreName: string;
  myStations: unknown[] | null;
  savedAt: number;
}

export function readHomeFeaturedCache(): HomeFeaturedCachePayload | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HomeFeaturedCachePayload;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeHomeFeaturedCache(partial: Partial<Omit<HomeFeaturedCachePayload, 'savedAt'>>): void {
  try {
    const prev = readHomeFeaturedCache();
    const next: HomeFeaturedCachePayload = {
      newReleases: partial.newReleases ?? prev?.newReleases ?? null,
      editorPicks: partial.editorPicks ?? prev?.editorPicks ?? null,
      featuredPlaylists: partial.featuredPlaylists ?? prev?.featuredPlaylists ?? null,
      pickedForYou: partial.pickedForYou ?? prev?.pickedForYou ?? null,
      popularGenre: partial.popularGenre ?? prev?.popularGenre ?? null,
      popularGenreName: partial.popularGenreName ?? prev?.popularGenreName ?? '',
      myStations: partial.myStations ?? prev?.myStations ?? null,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota */
  }
}

/** Synchronous hydrate path — used for first-paint budget tests. */
export function measureHomeCacheHydrateMs(): number {
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  readHomeFeaturedCache();
  const t1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return t1 - t0;
}

export function clearHomeFeaturedCacheForTests(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {}
}
